import * as RadixPopover from "@radix-ui/react-popover";
import { ChevronDown, Search, X } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Button } from "../../primitives/Button";
import { SURFACE } from "../../primitives/Menu";
import {
	matchOptions,
	SEARCHABLE_SELECT_THRESHOLD,
	SELECT_RENDER_CAP,
} from "../../primitives/Select";
import { Checkbox } from "../../primitives/Selection";
import { useUiText } from "../../primitives/uiText";
import { cx, FOCUS_RING, TOUCH_TARGET } from "../../styles";

/**
 * A filter chip that opens a checklist (ADJ-M03-1): "Status" when nothing is
 * chosen, "Status · Pending" for one, "Status · 3 selected" for more.
 *
 * The replacement for a `<select multiple>`, which needs Ctrl-click to pick a
 * second value, shows four rows of seventeen, and cannot be read at a glance.
 * The chip says what is filtered while closed; the popover has a search field
 * once the list is long enough to need one, a heading per `group`, a count
 * and a Clear. The chip's own × clears it without opening it.
 *
 * `single` (ADJ-M03-3) is the same chip for a filter the server takes one
 * value of — a store, a carrier, a province: picking one replaces the last and
 * closes the list.
 *
 * Controlled only: the value is the caller's — usually the URL — and a chip
 * that kept its own copy would be a second source of truth for the filter.
 */

export interface MultiSelectOption {
	value: string;
	label: string;
	/** A heading the option is listed under. Options of one group should be adjacent. */
	group?: string;
}

export interface MultiSelectProps {
	/** What is being filtered: "Status". The chip's name and the list's. */
	label: string;
	options: MultiSelectOption[];
	value: string[];
	onValueChange: (next: string[]) => void;
	/** One value at most: a pick replaces the last one and closes the list. */
	single?: boolean;
	/** A search field above the list. On by default for any non-empty list. */
	searchable?: boolean;
	/** Shown when there are no options at all (not when a search matches none). */
	emptyLabel?: string;
	disabled?: boolean;
	className?: string;
}

/** Past this many options the list gets a search field by default. */
export const SEARCH_FROM = SEARCHABLE_SELECT_THRESHOLD;

/**
 * The chip's summary of a choice: nothing, the one label, or a count.
 * Exported for its test.
 */
export function summarise(
	value: string[],
	options: MultiSelectOption[],
	count: (n: number) => string,
): string | null {
	if (value.length === 0) {
		return null;
	}
	if (value.length === 1) {
		return options.find((option) => option.value === value[0])?.label ?? value[0] ?? null;
	}
	return count(value.length);
}

/** The value with `option` added or taken out. Exported for its test. */
export function toggled(value: string[], option: string): string[] {
	return value.includes(option) ? value.filter((one) => one !== option) : [...value, option];
}

/** A single-value chip's next value: the option, or nothing when it was already chosen. */
export function picked(value: string[], option: string): string[] {
	return value.includes(option) ? [] : [option];
}

export interface FilterChipProps {
	/** What is being filtered: "Date". */
	label: string;
	/** What is chosen, in words; null draws the empty (dashed) chip. */
	summary: string | null;
	/** Clears the filter from the chip's own ×. */
	onClear: () => void;
	/** The popover's body. */
	children: ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** Focus the popover's own field on open instead of its first control. */
	onOpenAutoFocus?: (event: Event) => void;
	disabled?: boolean;
	className?: string;
	/** The popover's width. */
	contentClassName?: string;
}

/**
 * The chip itself (ADJ-M03-3): trigger, popover and ×, for a filter whose
 * body is not a checklist — a date range, a number. {@link MultiSelect} is
 * this with a checklist inside.
 */
export function FilterChip({
	label,
	summary,
	onClear,
	children,
	open,
	onOpenChange,
	onOpenAutoFocus,
	disabled = false,
	className,
	contentClassName = "w-64",
}: FilterChipProps) {
	const t = useUiText();
	const active = summary !== null;
	return (
		<div className={cx("inline-flex max-w-full items-stretch", className)}>
			<RadixPopover.Root
				{...(open === undefined ? {} : { open })}
				{...(onOpenChange === undefined ? {} : { onOpenChange })}
			>
				<RadixPopover.Trigger asChild>
					<button
						type="button"
						disabled={disabled}
						className={cx(
							"inline-flex h-9 min-w-0 cursor-pointer items-center gap-1.5 rounded-md border px-2.5",
							"text-[12.5px] font-semibold transition-colors motion-reduce:transition-none",
							"disabled:cursor-not-allowed disabled:text-fg-subtle",
							active
								? "rounded-e-none border-brand-subtle bg-brand-subtle text-fg"
								: "border-dashed border-border-strong bg-bg-surface text-fg-muted hover:bg-bg-subtle hover:text-fg",
							FOCUS_RING,
							TOUCH_TARGET,
						)}
					>
						<span className="shrink-0">{label}</span>
						{active && (
							<>
								<span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-border-strong" />
								<span className="min-w-0 max-w-48 truncate text-brand-ink">{summary}</span>
							</>
						)}
						<Icon as={ChevronDown} size={14} className="shrink-0 text-fg-subtle" />
					</button>
				</RadixPopover.Trigger>

				<RadixPopover.Portal>
					<RadixPopover.Content
						align="start"
						sideOffset={6}
						collisionPadding={8}
						className={cx(SURFACE, "flex max-w-[calc(100vw-16px)] flex-col", contentClassName)}
						{...(onOpenAutoFocus === undefined ? {} : { onOpenAutoFocus })}
					>
						{children}
					</RadixPopover.Content>
				</RadixPopover.Portal>
			</RadixPopover.Root>

			{active && (
				<button
					type="button"
					disabled={disabled}
					aria-label={t("ui.filters.clearOne", { label })}
					onClick={onClear}
					className={cx(
						"grid w-8 shrink-0 cursor-pointer place-items-center rounded-e-md border border-s-0",
						"border-brand-subtle bg-brand-subtle text-brand-ink hover:bg-bg-subtle",
						FOCUS_RING,
						TOUCH_TARGET,
					)}
				>
					<Icon as={X} size={14} />
				</button>
			)}
		</div>
	);
}

export function MultiSelect({
	label,
	options,
	value,
	onValueChange,
	single = false,
	searchable,
	emptyLabel,
	disabled = false,
	className,
}: MultiSelectProps) {
	const t = useUiText();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const input = useRef<HTMLInputElement>(null);
	const withSearch = searchable ?? options.length > SEARCH_FROM;

	const matches = useMemo(() => matchOptions(options, query), [options, query]);
	const shown = matches.slice(0, SELECT_RENDER_CAP);
	const summary = summarise(value, options, (count) => t("ui.filters.selected", { count }));

	function choose(option: string) {
		if (single) {
			onValueChange(picked(value, option));
			setOpen(false);
			return;
		}
		onValueChange(toggled(value, option));
	}

	return (
		<FilterChip
			label={label}
			summary={summary}
			onClear={() => onValueChange([])}
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) {
					setQuery("");
				}
			}}
			onOpenAutoFocus={(event) => {
				if (withSearch) {
					event.preventDefault();
					input.current?.focus();
				}
			}}
			disabled={disabled}
			{...(className === undefined ? {} : { className })}
		>
			{withSearch && (
				<div className="relative border-b border-border p-2">
					<Icon
						as={Search}
						size={14}
						className="pointer-events-none absolute inset-y-0 start-4 my-auto text-fg-subtle"
					/>
					<input
						ref={input}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={t("ui.actions.search")}
						aria-label={t("ui.actions.search")}
						className="h-8 w-full rounded-sm bg-transparent ps-7 pe-1 text-[13px] text-fg outline-none placeholder:text-fg-subtle"
					/>
				</div>
			)}

			<fieldset
				aria-label={label}
				data-scroll="1"
				className="m-0 flex max-h-72 min-w-0 flex-col overflow-y-auto border-0 p-1"
			>
				{shown.length === 0 && (
					<p className="p-3 text-[12px] text-fg-subtle">
						{options.length === 0 ? (emptyLabel ?? t("ui.select.empty")) : t("ui.select.noMatches")}
					</p>
				)}
				{shown.map((option, index) => (
					<div key={option.value} className="flex flex-col">
						{option.group !== undefined && option.group !== shown[index - 1]?.group && (
							<p className="px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
								{option.group}
							</p>
						)}
						<Checkbox
							checked={value.includes(option.value)}
							onCheckedChange={() => choose(option.value)}
							label={option.label}
							className="w-full rounded-sm px-2 py-1.5 hover:bg-bg-subtle"
						/>
					</div>
				))}
				{matches.length > shown.length && (
					<p className="px-2 py-1.5 text-[12px] text-fg-subtle">
						{t("ui.select.more", { count: matches.length - shown.length })}
					</p>
				)}
			</fieldset>

			{!single && (
				<div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
					<span className="text-[12px] text-fg-subtle [font-variant-numeric:tabular-nums]">
						{t("ui.filters.selected", { count: value.length })}
					</span>
					<Button
						size="sm"
						variant="ghost"
						disabled={value.length === 0}
						onClick={() => onValueChange([])}
					>
						{t("ui.actions.clear")}
					</Button>
				</div>
			)}
		</FilterChip>
	);
}
